"use client";

import React, { createContext, useContext, useState, useEffect } from 'react';
import { AuthUser } from '../types';
import { identifyCustomer, resetAnalytics, trackEvent } from '../lib/posthog';
import { supabaseAnonKey, supabaseUrl } from '../lib/supabaseConfig';

interface AuthContextType {
  user: AuthUser | null;
  isLoggedIn: boolean;
  login: (phone: string, token: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_TOKEN_KEY = 'astroji_auth_token_v2';

const parseJwt = (token: string) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchCustomerDetails = async (phone: string, token: string) => {
    try {
      const response = await fetch(`${supabaseUrl}/functions/v1/order-lookup`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          apikey: supabaseAnonKey,
          Authorization: `Bearer ${supabaseAnonKey}`,
          'X-Astroji-Auth': `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'track', phone }),
      });
      if (!response.ok) return undefined;

      const data = await response.json();

      if (data?.success && data?.customer) {
        return data.customer.name;
      }
    } catch (err) {
      console.error('Error fetching customer details for Auth:', err);
    }
    return undefined;
  };

  useEffect(() => {
    const initializeAuth = async () => {
      setIsLoading(true);
      try {
        const storedToken = localStorage.getItem(AUTH_TOKEN_KEY);
        
        if (storedToken) {
          const payload = parseJwt(storedToken);
          // Check if token is valid and not expired
          if (payload && payload.sub && payload.exp * 1000 > Date.now()) {
            const phone = payload.phone || payload.sub;
            setUser({ phone });
            await identifyCustomer(payload.sub || phone);

            const name = await fetchCustomerDetails(phone, storedToken);
            setUser({ phone, name: name || undefined });
          } else {
            // Token is invalid or expired
            localStorage.removeItem(AUTH_TOKEN_KEY);
          }
        }
      } catch (err) {
        console.error('Error initializing auth:', err);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (phone: string, token: string) => {
    trackEvent('login_attempted', { login_method: 'otp' });
    try {
      localStorage.setItem(AUTH_TOKEN_KEY, token);
      const payload = parseJwt(token);
      await identifyCustomer(payload?.sub || phone);
      setUser({ phone });

      // Fetch customer details in background
      const name = await fetchCustomerDetails(phone, token);
      setUser({ phone, name: name || undefined });
      
      trackEvent('login_completed', { login_method: 'otp' });
    } catch (error) {
      trackEvent('login_failed', { login_method: 'otp', error: String(error) });
      throw error;
    }
  };

  const logout = () => {
    trackEvent('user_logged_out');
    resetAnalytics();
    localStorage.removeItem(AUTH_TOKEN_KEY);
    // Remove legacy keys if they exist
    localStorage.removeItem('astroji_auth_phone');
    localStorage.removeItem('astroji_tracked_phone');
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoggedIn: !!user,
        login,
        logout,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
